import {
  Persona,
  PersonaPresence,
  PersonaSize,
  Text,
  mergeStyleSets,
  Icon,
  ActionButton,
  Label,
  Link,
  Dialog,
  DialogFooter,
  DialogType,
  DefaultButton,
} from "@fluentui/react";
import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { useSelector } from "../../../../store/hooks";
import {
  formatDate,
  getFileExtension,
  getFileIcon,
  getIconColor,
} from "../../../../utils/utils";
import moment from "moment";
import { Flex, Truncate } from "components/styled";
import styled from "styled-components";
import { formatDateStringWithTime } from "utils/date";

export const Description = () => {
  const { t } = useTranslation();
  const recommendation = useSelector((s) => s.recommendationPage.rec);
  const attachments = useSelector((s) => s.recommendationPage.attachments);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [activeDocUri, setActiveDocUri] = useState<string | undefined>();

  const documents = useMemo(() => {
    return (
      attachments
        ?.filter((att) => att.attachmentPath)
        .map((att) => ({
          uri: att.attachmentPath as string,
          fileName: att.attachmentName,
          fileType: att.attachmentMimeType || undefined,
        })) || []
    );
  }, [attachments]);

  const uploadedAtByUri = useMemo(() => {
    const map = new Map<string, string>();
    attachments
      ?.filter((att) => att.attachmentPath)
      .forEach((att) => {
        if (att.attachmentCreatedAt) {
          map.set(att.attachmentPath as string, String(att.attachmentCreatedAt));
        }
      });
    return map;
  }, [attachments]);

  const handleDocumentChange = useCallback(
    (doc: { uri: string }) => setActiveDocUri(doc.uri),
    [],
  );

  const docViewerConfig = useMemo(
    () => ({
      header: { disableHeader: false, disableFileName: false },
      pdfZoom: { defaultZoom: 1, zoomJump: 0.1 },
      pdfVerticalScrollByDefault: true,
    }),
    [],
  );

  const docViewerElement = useMemo(
    () => (
      <DocViewer
        key={documents.map((d) => d.uri).join(",")}
        documents={documents}
        pluginRenderers={DocViewerRenderers}
        prefetchMethod="GET"
        onDocumentChange={handleDocumentChange}
        config={docViewerConfig}
      />
    ),
    [documents, handleDocumentChange, docViewerConfig],
  );

  const dialogContentProps = {
    type: DialogType.normal,
    title: t("ram.responsesPage.attachments.previewDocuments"),
    closeButtonAriaLabel: "Close",
  };

  return (
    <div className={classes.container} id="responses-tab-description">
      {recommendation?.title ? (
        <div className={classes.gridLeft}>
          <div>
            <Label>{t("ram.recommendationForm.title.label")}</Label>
            <Text>{recommendation?.title} </Text>
          </div>
        </div>
      ) : null}
      <div className={classes.gridLeft}>
        <div>
          <Persona
            title={recommendation?.recommendationCreatedByUserName}
            text={recommendation?.recommendationCreatedByUserName}
            presence={PersonaPresence.online}
            styles={{
              root: {
                width: "40px",
                alignSelf: "flex-start",
                marginTop: "10px",
              },
            }}
            size={PersonaSize.size40}
          />
        </div>
        <div>
          <Label>
            {t("ram.recommendationForm.dateOfRecommendation.label")}
          </Label>
          <Text>
            {formatDate(
              moment(recommendation?.dateOfRecommendation).toDate(),
            )}{" "}
          </Text>
        </div>
      </div>
      {recommendation?.recommendationNumberInternal ||
      recommendation?.recommendationNumberExternal ? (
        <div className={classes.grid}>
          <div>
            <Label>
              {t("ram.recommendationForm.recommendationNumberInternal.label")}
            </Label>
            <Text>{recommendation?.recommendationNumberInternal} </Text>
          </div>
          <div>
            <Label>
              {t("ram.recommendationForm.recommendationNumberExternal.label")}
            </Label>
            <Text>{recommendation?.recommendationNumberExternal} </Text>
          </div>
        </div>
      ) : null}
      {recommendation?.description ? (
        <div className={classes.fullWidth}>
          <Label>{t("ram.recommendationForm.description.label")}</Label>
          <Text style={{ whiteSpace: "pre-wrap" }}>
            {recommendation?.description}
          </Text>
        </div>
      ) : null}
      {attachments?.length > 0 ? (
        <div className={classes.fullWidth}>
          <AttachmentLabelRow>
            <Label>{t("ram.responsesPage.attachments")}</Label>
            {documents.length > 0 && (
              <ActionButton
                iconProps={{ iconName: "View" }}
                allowDisabledFocus
                onClick={() => {
                  setActiveDocUri(documents[0]?.uri);
                  setIsPreviewDialogOpen(true);
                }}
              >
                {t("ram.responsesPage.attachments.previewDocuments")}
              </ActionButton>
            )}
          </AttachmentLabelRow>

          <AttachmentListContainer>
            {attachments.map((att, index) => {
              return (
                <AttachmentListItem key={att.attachmentName}>
                  <Icon
                    iconName={getFileIcon(getFileExtension(att.attachmentName))}
                    style={{
                      color: getIconColor(getFileExtension(att.attachmentName)),
                    }}
                  />
                  <AttachmentName>
                    {att.attachmentPath ? (
                      <Link href={att.attachmentPath} target="_blank">
                        {att.attachmentName}
                      </Link>
                    ) : (
                      att.attachmentName
                    )}
                  </AttachmentName>
                </AttachmentListItem>
              );
            })}
          </AttachmentListContainer>
          <Dialog
            hidden={!isPreviewDialogOpen}
            onDismiss={() => setIsPreviewDialogOpen(false)}
            dialogContentProps={dialogContentProps}
            minWidth={800}
            maxWidth={1200}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Label style={{ margin: 0 }}>
                  {t("ram.recommendationForm.dateOfRecommendation.label")}:
                </Label>
                <span>
                  {recommendation?.dateOfRecommendation
                    ? formatDate(
                        moment(recommendation.dateOfRecommendation).toDate(),
                      )
                    : ""}
                </span>
              </div>
              {activeDocUri && uploadedAtByUri.get(activeDocUri) && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Label style={{ margin: 0 }}>
                    {t("ram.responsesPage.attachments.uploadedAt")}:
                  </Label>
                  <span>
                    {formatDateStringWithTime(uploadedAtByUri.get(activeDocUri)!)}
                  </span>
                </div>
              )}
            </div>
            <DocViewerContainer>
              {docViewerElement}
            </DocViewerContainer>
            <DialogFooter>
              <DefaultButton
                onClick={() => setIsPreviewDialogOpen(false)}
                text={t("ram.reportDocumentDialogDownloadClose.label")}
              />
            </DialogFooter>
          </Dialog>
        </div>
      ) : null}
    </div>
  );
};

const classes = mergeStyleSets({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  grid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: "20px",
    overfow: "auto",
  },
  gridLeft: {
    width: "100%",
    display: "flex",
    justifyContent: "left",
    gap: "10px",
    flexDirection: "row",
  },
  fullWidth: {
    width: "100%",
  },
  label: {},
});
const Email = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.palette.neutralSecondary};
`;

const AttachmentListContainer = styled.ul`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
  margin-bottom: 15px;
`;
const AttachmentListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLighterAlt};
  width: 350px;
`;

const AttachmentName = styled.div`
  font-size: 14px;
  color: ${(p) => p.theme.palette.neutralPrimary};
  flex: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 300px;
`;

const AttachmentLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DocViewerContainer = styled.div`
  width: 100%;
  height: 80vh;
  min-height: 600px;
  overflow: auto;

  #react-doc-viewer {
    height: 100% !important;
  }

  #proxy-renderer {
    height: 100% !important;
  }

  #msdoc-renderer,
  #image-renderer,
  #pdf-renderer {
    height: 100% !important;
  }

  iframe {
    height: 100% !important;
    min-height: 550px;
  }
`;
